import { useEffect, useState } from 'react'
import { Modal, Button, Group, Text, Stack } from '@mantine/core'
import { useSession } from './context'

function useCountdown(deadline: number | null): number {
  const [remaining, setRemaining] = useState(() =>
    deadline ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000)) : 0,
  )

  useEffect(() => {
    if (!deadline) {
      setRemaining(0)
      return
    }

    setRemaining(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)))

    const interval = setInterval(() => {
      const seconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
      setRemaining(seconds)
    }, 1000)

    return () => clearInterval(interval)
  }, [deadline])

  return remaining
}

function formatCountdown(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60)
  const secs = totalSeconds % 60
  return `${mins}:${String(secs).padStart(2, '0')}`
}

function InactivityWarningModal() {
  const { status, deadline, stayActive, logout } = useSession()
  const opened = status === 'inactivity-warning'
  const remaining = useCountdown(opened ? deadline : null)

  return (
    <Modal
      opened={opened}
      onClose={stayActive}
      title="Session Expiring"
      centered
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
    >
      <Stack gap="md">
        <Text>
          You've been inactive. Your session will expire in{' '}
          <Text component="span" fw={700}>
            {formatCountdown(remaining)}
          </Text>
          .
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={logout}>
            Log Out
          </Button>
          <Button onClick={stayActive}>Stay Logged In</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function AbsoluteWarningModal() {
  const { status, deadline, logout } = useSession()
  const opened = status === 'absolute-warning'
  const remaining = useCountdown(opened ? deadline : null)

  return (
    <Modal
      opened={opened}
      onClose={() => {}}
      title="Session Limit Reached"
      centered
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
    >
      <Stack gap="md">
        <Text>
          Your session has been active for the maximum allowed duration. You will be signed out in{' '}
          <Text component="span" fw={700}>
            {formatCountdown(remaining)}
          </Text>
          . Please re-authenticate to continue.
        </Text>
        <Group justify="flex-end">
          <Button onClick={logout}>Re-authenticate</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export function SessionWarningModals() {
  return (
    <>
      <InactivityWarningModal />
      <AbsoluteWarningModal />
    </>
  )
}
